import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { ActivityLogItem, NoticiationIcon } from './activity-schema'
import { SvgIcons } from '../../../../voyage-svg-icons/src/voyage-svg-icons'
import { prettyTimelapsed } from '../../../../../src/utils/helpers'
import '@maersk-global/mds-components-core/mc-icon'
import { NotificationTypesExtended } from '../../../../../src/models/types/vessel-notifications.types'

//**
// ---- PROGRESS BAR COMPONENT ----
// *
@customElement('progress-bar')
export class ProgressBarElement extends LitElement {
    @property({ type: String }) status = 'submitted'

    static styles = css`
        :host {
            font-size: 11px;
        }

        .progress-container {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            gap: 8px;
        }

        .status {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
        }

        .progress-line {
            height: 1px;
            background-color: #e0e0e0;
            flex: 1;
        }
    `

    render() {
        return html`
            <div class="progress-container">
                <div class="status">
                    <mc-icon
                        color="#01233c"
                        icon="check-circle-solid"
                    ></mc-icon>
                    <span>Synced</span>
                </div>
                <div class="progress-line"></div>
                <div class="status">
                    <mc-icon
                        color="#01233c"
                        icon=${this.status === 'complete'
                            ? 'check-circle-solid'
                            : 'check-circle'}
                    ></mc-icon>
                    <span>Synced & Sent</span>
                </div>
            </div>
        `
    }
}

//**
// ---- ACTION BUTTON COMPONENT ----
// *
@customElement('action-button')
export class ActionButtonElement extends LitElement {
    @property({ type: String }) label = ''
    @property({ type: String }) variant = 'primary'
    @property({ type: String }) action = ''
    @property({ type: Object }) data = {}

    static styles = css``

    handleClick() {
        // Dispatch a custom event with the action and data
        const customEvent = new CustomEvent(this.action, {
            detail: this.data,
            bubbles: true,
            composed: true,
        })
        this.dispatchEvent(customEvent)
    }

    render() {
        return html`
            <button class=${this.variant} @click=${this.handleClick}>
                ${this.label}
            </button>
        `
    }
}

//**
// ---- NOTIFICATION SLOT COMPONENT ----
// *
@customElement('notification-slot')
export class NotificationSlotElement extends LitElement {
    @property({ type: Object }) notification!: ActivityLogItem

    static styles = css`
        :host {
            display: block;
            margin-top: 12px;
        }
    `

    render() {
        // determine which additional component slot to render based on notification type (if any)
        switch (this.notification.type) {
            case 'sync':
                return html`
                    <progress-bar
                        .status=${this.notification.status}
                    ></progress-bar>
                `
            // TO DO: add rest here

            default:
                return null
        }
    }
}

//**
// ---- NOTIFICATION ITEM CONTENT ----
// *

@customElement('notification-item-content')
export class NotificationItemContentElement extends LitElement {

    @property({ type: Object }) notification!: ActivityLogItem;

    static styles = css`
        :host {
            display: block;
        }
        .notification-grid {
            display: grid;
            grid-template-columns: 26px 1fr;
            gap: 6px;
            margin: 0px 10px;
            padding: 10px 0px;
            border-bottom: 1px solid #e0dfdf;
        }

        .notification-content {
            display: flex;
            flex-direction: column;
        }
        .notification-content.info h4{
            margin:0;
        }
        .notification-content.info span{
            font-size: 14px;
        }    
        .title {
            font-size: 16px;
            font-family: 'Maersk Text';
            line-height: 20px;
        }

        .requires-action .title {
            font-weight: bold;
        }

        .icon {
            border-radius: 50%;
            height: 26px;
            width: 26px;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .icon--custom {
            width: 18px;
            height: 18px;
            color: #ffffff;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .size16::part(icon) {
            transform: scale(1.2);
        }

        .timestamp {
            margin-left: auto;
            color: var(--neutrals-grey-04);
            font-size: 12px;
        }
    `

    // rendering icon based on notification type
    renderIcon(icon: NoticiationIcon) {
        return html`<div
        class="icon"
            style=${styleMap({ backgroundColor: icon.bgColor })}
        >
            ${icon.name
                ? html`<mc-icon
                      icon=${icon.name}
                      color=${icon.color}
                      size="16"
                  ></mc-icon>`
                : icon.customName
                ? html` <span class="icon--custom"
                      >${SvgIcons[icon.customName]}</span
                  >`
                : null}
        </div>`
    }

    render() {
        switch (this.notification.type) {
        case NotificationTypesExtended.Sync:
            return html`
                <div class="notification-grid">
                    ${this.renderIcon(this.notification?.icon)}
                    <div class="notification-content">
                        <div class="header">
                            <span class="title">${this.notification.title}</span>
                            ${this.notification.description
                                ? html`<p class="description">
                                        ${this.notification.description}
                                    </p>`
                                : null}
                        </div>

                        <notification-slot .notification=${this.notification}></notification-slot>
                        <span class="timestamp">${prettyTimelapsed(this.notification.created_at)}</span>
                    </div>
                </div>
            `
        case NotificationTypesExtended.BasicPlan:
            return html`
                <div class="notification-grid">
                    ${this.renderIcon(this.notification?.icon)}
                    <div class="notification-content info">
                            <h4>${this.notification.details.title}</h4>
                            <span>${this.notification.details.message}</span>
                            <span class="timestamp">${prettyTimelapsed(this.notification.created_at)}</span>
                    </div>
                </div>
            `
        case NotificationTypesExtended.HeavyWeather:
        case NotificationTypesExtended.ParametricRoll:
        case NotificationTypesExtended.HeavyRolling:
        case NotificationTypesExtended.HighAccelerationForce:
            return html`
                <div class="notification-grid">
                    ${this.renderIcon(this.notification?.icon)}
                    <div class="notification-content info">
                        <h4>${this.notification.title}</h4>
                        <span>${this.notification.description}</span>
                        <span class="timestamp">${prettyTimelapsed(this.notification.created_at)}</span>
                    </div>
                </div>
            `
        default:
            return null
    }
    }
}

//**
// ---- NOTIFICATION ITEM COMPONENT ----
// *
@customElement('notification-item')
export class NotificationItemElement extends LitElement {
    @property({ type: Object }) notification!: ActivityLogItem

    static styles = css`
        :host {
            display: block;
        }

        .requires-action {
            background-color: rgba(66, 176, 213, 0.11);
        }

        .requires-action .title {
            font-weight: bold;
        }
    `

    render() {
        const notificationClasses = {
            'requires-action': !!this.notification.requiresAction,
            notification: true,
        }

        return html`
            <div class=${classMap(notificationClasses)}>
                <notification-item-content .notification=${this.notification}/>
            </div>
        `
    }
}
